<ng-container *ngTemplateOutlet="type === 'list' ? asListItem : asCard"></ng-container>

<ng-template #asListItem>
  <dcr-list-item
    [canSelect]="canShowSelect$ | async"
    [attr.hero-type]="data.bestResult?.attempts?.length || data.sessions?.length ? 'score' : undefined"
    [headline]="fullEduContentName"
    [supportingText]="data.eduContent.description"
    [leadingIcon]="data.eduContent.icon ? 'educontent:' + data.eduContent.icon : undefined"
    (selection-change)="onSelectionChange($event.detail.checked, data)"
    [selected]="selected || (inBasket && (canManageTaskContentV2$ | async) === false)"
  >
    @if (primaryAction || secondaryActions?.length) {
      <div slot="trailing-actions" style="--dcr-comp-card-actions-justify-content: space-between">
        <div>
          <dcr-outline-icon-button
            *hasPermission="Permissions.MANAGE_FAVORITES"
            data-cy="toc-result-favorite"
            (click)="onToggleFavorite()"
            [icon]="isFavorite ? 'shared:favorite:filled' : 'shared:favorite'"
            [attr.data-cy]="isFavorite ? 'favorite-icon-filled' : 'favorite-icon'"
            class="[ hover:icon-primary hover:border-primary ]"
            [ngClass]="{
              'animate-jello-horizontal duration-long-4 icon-primary': isFavorite,
            }"
          ></dcr-outline-icon-button>
        </div>
        <div class="flex flex-wrap gap-2xs items-center justify-end">
          @if (secondaryActions?.length === 1 && !secondaryActions[0].subActions?.length) {
            <dcr-inline-button
              data-cy="search-result-action"
              [leadingIcon]="secondaryActions[0].icon ? 'content-open-actions:' + secondaryActions[0].icon : undefined"
              [label]="secondaryActions[0].label"
              (click)="onActionClick(secondaryActions[0])"
            ></dcr-inline-button>
          }
          @if (primaryAction) {
            <dcr-tonal-button
              color="secondary"
              [label]="primaryAction.label"
              [leadingIcon]="primaryAction.icon ? 'content-open-actions:' + primaryAction.icon : undefined"
              (click)="onActionClick(primaryAction)"
            ></dcr-tonal-button>
          }
          @if (secondaryActions?.length > 1 || (secondaryActions?.length === 1 && secondaryActions[0].subActions?.length)) {
            <div class="toc-result-list-item__actions">
              <dcr-inline-icon-button
                data-cy="btn-tli-action"
                icon="shared:more"
                [attr.id]="'trli-more-' + data.eduContent.id"
              ></dcr-inline-icon-button>
              <dcr-menu [anchor]="'trli-more-' + data.eduContent.id" positioning="popover" anchor-corner="end-end" menu-corner="start-end">
                @for (action of secondaryActions; track action) {
                  @if (action.subActions?.length) {
                    <dcr-sub-menu>
                      <dcr-menu-item
                        slot="item"
                        data-cy="search-result-action"
                        [leadingIcon]="action.icon ? 'content-open-actions:' + action.icon : undefined"
                        [label]="action.label"
                      ></dcr-menu-item>
                      <dcr-menu slot="menu" positioning="popover">
                        @for (subAction of action.subActions; track subAction) {
                          <dcr-menu-item
                            data-cy="search-result-sub-action"
                            [leadingIcon]="subAction.icon ? 'content-open-actions:' + subAction.icon : undefined"
                            [label]="subAction.label"
                            [attr.disabled]="subAction.disabled ? '' : null"
                            (click)="onActionClick(subAction)"
                          ></dcr-menu-item>
                        }
                      </dcr-menu>
                    </dcr-sub-menu>
                  } @else {
                    <dcr-menu-item
                      data-cy="search-result-action"
                      [leadingIcon]="'content-open-actions:' + action.icon"
                      [label]="action.label"
                      (click)="onActionClick(action)"
                    ></dcr-menu-item>
                  }
                }
              </dcr-menu>
            </div>
          }
        </div>
      </div>
    }

    @if (data.eduContent?.isScorable && (data.bestResult?.attempts?.length || data.sessions?.length)) {
      <div class="toc-result-list-item__score" slot="hero">
        @if (data.bestResult?.attempts?.length && !data.sessions?.length) {
          <dcr-score
            [value]="data.bestResult.score"
            [manualGrading]="data.bestResult.needsManualGrading"
            [attempts]="attemptScores"
          ></dcr-score>
        }
        @if (data.sessions?.length) {
          <campus-score-level-attempts
            (click)="onClickLevelAttempts()"
            [scoreLevels]="data.sessions"
            class="toc-result-list-item__score-level-attempts"
          ></campus-score-level-attempts>
        }
      </div>
    }

    <div class="flex flex-wrap gap-m items-center justify-end" slot="trailing-content">
      <ng-container *hasPermission="Permissions.MANAGE_TASKS">
        @if (data.inTaskAmount) {
          <div class="relative">
            <dcr-icon data-cy="list-item-in-task" icon="shared:task"></dcr-icon>
            <dcr-badge [count]="data.inTaskAmount" class="absolute -top-xs -right-xs"></dcr-badge>
          </div>
        }
      </ng-container>
      <ng-container *hasPermission="Permissions.MANAGE_BUNDLES">
        @if (data.inBundleAmount) {
          <div class="relative">
            <dcr-icon data-cy="list-item-in-bundle" icon="shared:bundle"></dcr-icon>
            <dcr-badge [count]="data.inBundleAmount" class="absolute -top-xs -right-xs"></dcr-badge>
          </div>
        }
      </ng-container>

      @if (data.eduContent.publishedEduContentMetadata?.showInFreePractice) {
        <campus-chip-group class="toc-result-list-item__practice">
          <campus-suggestion-chip
            *hasPermission="'showFreePractice'"
            data-cy="search-result-practice"
            [label]="'teacher.searchResult.zelfLeren' | textMapping"
          ></campus-suggestion-chip>
        </campus-chip-group>
      }
    </div>
  </dcr-list-item>
</ng-template>

<ng-template #asCard>
  <dcr-card
    [headline]="fullEduContentName"
    [subhead]="data.eduContent.description"
    [attr.hero-type]="data.bestResult?.attempts?.length || data.sessions?.length ? 'score' : undefined"
    orientation="horizontal"
    [leadingIcon]="data.eduContent.icon ? 'educontent:' + data.eduContent.icon : undefined"
    [ariaSelected]="selected"
    [elevated]="selected"
    [attr.color]="selected ? 'secondary' : undefined"
    style="--dcr-comp-card-max-width: 100%"
    class="h-full"
  >
    <div slot="header-action" *hasPermission="[[Permissions.MANAGE_TASKS, Permissions.MANAGE_BUNDLES]]">
      @if (inBasket && (canManageTaskContentV2$ | async) === false) {
        <campus-icon class="toc-result-list-item__checkbox" svgIcon="shopping-bag"></campus-icon>
      }

      @if ((canShowSelect$ | async) === true) {
        <dcr-checkbox
          data-cy="checkbox-toc-result"
          class="toc-result-list-item__checkbox"
          [checked]="selected"
          (change)="onSelectionChange($event.target.checked, data)"
        ></dcr-checkbox>
      }
    </div>

    @if (primaryAction || secondaryActions?.length) {
      <div slot="actions" style="--dcr-comp-card-actions-justify-content: space-between">
        <div>
          <dcr-outline-icon-button
            *hasPermission="Permissions.MANAGE_FAVORITES"
            data-cy="toc-result-favorite"
            (click)="onToggleFavorite()"
            [icon]="isFavorite ? 'shared:favorite:filled' : 'shared:favorite'"
            [attr.data-cy]="isFavorite ? 'favorite-icon-filled' : 'favorite-icon'"
            class="[ hover:icon-primary hover:border-primary ]"
            [ngClass]="{
              'animate-jello-horizontal duration-long-4 icon-primary': isFavorite,
            }"
          ></dcr-outline-icon-button>
        </div>
        <div class="flex flex-wrap gap-2xs items-center justify-end">
          @if (secondaryActions?.length === 1 && !secondaryActions[0].subActions?.length) {
            <dcr-inline-button
              data-cy="search-result-action"
              [leadingIcon]="secondaryActions[0].icon ? 'content-open-actions:' + secondaryActions[0].icon : undefined"
              [label]="secondaryActions[0].label"
              (click)="onActionClick(secondaryActions[0])"
            ></dcr-inline-button>
          }
          @if (primaryAction) {
            <dcr-tonal-button
              color="secondary"
              [label]="primaryAction.label"
              [leadingIcon]="primaryAction.icon ? 'content-open-actions:' + primaryAction.icon : undefined"
              (click)="onActionClick(primaryAction)"
            ></dcr-tonal-button>
          }
          @if (secondaryActions?.length > 1 || (secondaryActions?.length === 1 && secondaryActions[0].subActions?.length)) {
            <div class="toc-result-list-item__actions">
              <dcr-inline-icon-button
                data-cy="btn-tli-action"
                icon="shared:more"
                [attr.id]="'trli-more-' + data.eduContent.id"
              ></dcr-inline-icon-button>
              <dcr-menu [anchor]="'trli-more-' + data.eduContent.id" positioning="popover" anchor-corner="end-end" menu-corner="start-end">
                @for (action of secondaryActions; track action) {
                  @if (action.subActions?.length) {
                    <dcr-sub-menu>
                      <dcr-menu-item
                        slot="item"
                        data-cy="search-result-action"
                        [leadingIcon]="action.icon ? 'content-open-actions:' + action.icon : undefined"
                        [label]="action.label"
                      ></dcr-menu-item>
                      <dcr-menu slot="menu" positioning="popover">
                        @for (subAction of action.subActions; track subAction) {
                          <dcr-menu-item
                            data-cy="search-result-sub-action"
                            [leadingIcon]="subAction.icon ? 'content-open-actions:' + subAction.icon : undefined"
                            [label]="subAction.label"
                            [attr.disabled]="subAction.disabled ? '' : null"
                            (click)="onActionClick(subAction)"
                          ></dcr-menu-item>
                        }
                      </dcr-menu>
                    </dcr-sub-menu>
                  } @else {
                    <dcr-menu-item
                      data-cy="search-result-action"
                      [leadingIcon]="'content-open-actions:' + action.icon"
                      [label]="action.label"
                      (click)="onActionClick(action)"
                    ></dcr-menu-item>
                  }
                }
              </dcr-menu>
            </div>
          }
        </div>
      </div>
    }

    @if (data.eduContent?.isScorable && (data.bestResult?.attempts?.length || data.sessions?.length)) {
      <div class="toc-result-list-item__score" slot="hero">
        @if (data.bestResult?.attempts?.length && !data.sessions?.length) {
          <dcr-score
            [value]="data.bestResult.score ?? undefined"
            [manualGrading]="data.bestResult.needsManualGrading"
            [attempts]="attemptScores"
          ></dcr-score>
        }
        @if (data.sessions?.length) {
          <campus-score-level-attempts
            (click)="onClickLevelAttempts()"
            [scoreLevels]="data.sessions"
            class="toc-result-list-item__score-level-attempts"
          ></campus-score-level-attempts>
        }
      </div>
    }

    <div class="flex flex-wrap gap-m items-center justify-end" slot="header">
      <ng-container *hasPermission="Permissions.MANAGE_TASKS">
        @if (data.inTaskAmount) {
          <div class="relative">
            <dcr-icon data-cy="list-item-in-task" icon="shared:task"></dcr-icon>
            <dcr-badge [count]="data.inTaskAmount" class="absolute -top-xs -right-xs"></dcr-badge>
          </div>
        }
      </ng-container>
      <ng-container *hasPermission="Permissions.MANAGE_BUNDLES">
        @if (data.inBundleAmount) {
          <div class="relative">
            <dcr-icon data-cy="list-item-in-bundle" icon="shared:bundle"></dcr-icon>
            <dcr-badge [count]="data.inBundleAmount" class="absolute -top-xs -right-xs"></dcr-badge>
          </div>
        }
      </ng-container>

      @if (data.eduContent.publishedEduContentMetadata?.showInFreePractice) {
        <campus-chip-group class="toc-result-list-item__practice">
          <campus-suggestion-chip
            *hasPermission="'showFreePractice'"
            data-cy="search-result-practice"
            [label]="'teacher.searchResult.zelfLeren' | textMapping"
          ></campus-suggestion-chip>
        </campus-chip-group>
      }
    </div>
  </dcr-card>
</ng-template>
